// =============================================================================
// Pintravel — Filling the offline cache on purpose
// =============================================================================
// Before this, a trip was only readable offline if you happened to have opened
// it while you had signal. Which trip that turned out to be was an accident of
// where you had tapped — and the one you need in a tunnel is rarely the one you
// browsed last week.
//
// So the app fetches them itself. Not all at once and not at startup: the home
// screen's own list goes first and is left alone to render, then the full
// itineraries trickle in behind it, a couple at a time, ordered by how likely
// you are to need them. A trip already sitting complete on disk is skipped.
// =============================================================================

import type { QueryClient } from '@tanstack/react-query';

import type { TripDetailResponse, TripResponse } from '@/features/trips/api/trips.api';
import { tripKeys } from '@/features/trips/hooks/useTrips';
import { offlineDb } from '@/lib/db/offline-db';

/** How many detail requests are in flight at once. */
const CONCURRENCY = 2;

/** Breathing room after the home screen's own query before we add traffic. */
const START_DELAY_MS = 1_500;

/** A complete trip re-downloaded sooner than this is wasted bandwidth. */
const REFRESH_AFTER_MS = 1000 * 60 * 60 * 6;

/** Trips beyond this are left to load on demand — see the note in run(). */
const MAX_TRIPS = 40;

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

export type TripPriority = 'ongoing' | 'upcoming' | 'recent' | 'unscheduled' | 'past';

export function tripPriority(
  trip: Pick<TripResponse, 'startDate' | 'endDate'>,
  now: Date,
): TripPriority {
  // Kept for later, so not urgent — but still more alive than 2019.
  if (!trip.startDate || !trip.endDate) return 'unscheduled';
  const start = new Date(trip.startDate);
  const end = new Date(trip.endDate);

  if (now >= start && now <= end) return 'ongoing';
  if (now < start) return 'upcoming';

  // A trip that ended in the last fortnight is still being settled up and
  // written about, so it is worth more than one from two years ago.
  const daysSinceEnd = (now.getTime() - end.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceEnd <= 14 ? 'recent' : 'past';
}

const PRIORITY_ORDER: Record<TripPriority, number> = {
  ongoing: 0,
  upcoming: 1,
  recent: 2,
  unscheduled: 3,
  past: 4,
};

/**
 * The trip you are on beats the one you leave for on Friday, which beats the
 * one you got back from, which beats 2019. Within a band, nearer in time first.
 */
export function prioritiseTrips<T extends Pick<TripResponse, 'startDate' | 'endDate'>>(
  trips: T[],
  now: Date,
): T[] {
  return [...trips].sort((a, b) => {
    const byBand = PRIORITY_ORDER[tripPriority(a, now)] - PRIORITY_ORDER[tripPriority(b, now)];
    if (byBand !== 0) return byBand;

    const distance = (trip: T) =>
      trip.startDate ? Math.abs(new Date(trip.startDate).getTime() - now.getTime()) : 0;
    return distance(a) - distance(b);
  });
}

// ---------------------------------------------------------------------------
// What still needs fetching
// ---------------------------------------------------------------------------

export interface CachedTripIndexEntry {
  id: string;
  syncedAt: string | null;
  hasDetail: boolean;
}

export function needsPrefetch(
  tripId: string,
  index: CachedTripIndexEntry[],
  now: Date,
  refreshAfterMs = REFRESH_AFTER_MS,
): boolean {
  const entry = index.find((candidate) => candidate.id === tripId);

  // Never stored, or stored as a summary the list endpoint produced — that
  // record has a name and dates and no itinerary, which is the part that
  // matters without signal.
  if (!entry || !entry.hasDetail) return true;
  if (!entry.syncedAt) return true;

  const age = now.getTime() - new Date(entry.syncedAt).getTime();
  return Number.isNaN(age) || age > refreshAfterMs;
}

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

export interface PrefetchResult {
  fetched: number;
  skipped: number;
  failed: number;
}

export interface PrefetchOptions {
  /** Aborted on logout, on going offline, and on account switch. */
  signal?: AbortSignal;
  /** Overridable so tests do not wait. */
  startDelayMs?: number;
  now?: Date;
}

/**
 * The money, alongside the itinerary.
 *
 * Fetched here rather than left to the screens, because the expenses tab is
 * exactly the one you open in a restaurant with no signal — and if it only
 * caches what you have already visited, it is empty precisely when it matters.
 *
 * Failures are swallowed one by one: a trip whose balance will not load still
 * gets its itinerary stored.
 */
async function prefetchExpenses(tripId: string, signal?: AbortSignal): Promise<void> {
  const { api } = await import('@/lib/api/instance');

  const slots: Array<[string, string]> = [
    ['list', `trips/${tripId}/expenses`],
    ['balance', `trips/${tripId}/expenses/balance`],
    ['settlements', `trips/${tripId}/expenses/settlements`],
    ['stats', `trips/${tripId}/expenses/stats`],
  ];

  for (const [slot, url] of slots) {
    if (signal?.aborted) return;
    try {
      const body = await api.get(url).json();
      await offlineDb.cacheTripExpenses(tripId, { [slot]: body });
    } catch {
      // Leave it; the screen's own fetch will cache it if it ever loads.
    }
  }
}

/**
 * How many of a trip's photos get their thumbnail stored. A thumbnail is about
 * 30 KB, so this is roughly 3 MB per trip — enough that a gallery opened on a
 * plane has something to show, and not so much that a background task quietly
 * fills the phone.
 */
const MAX_CACHED_THUMBNAILS_PER_TRIP = 100;

/**
 * Pulls the photo list and stores the newest thumbnails as files.
 *
 * The list alone would leave the grid full of broken tiles offline, since the
 * images live on a bucket rather than in the response.
 */
async function prefetchPhotos(tripId: string, signal?: AbortSignal): Promise<void> {
  const [{ api }, { savePhotoFile }] = await Promise.all([
    import('@/lib/api/instance'),
    import('@/lib/photos/photo-file-store'),
  ]);

  let listed: { photos: Array<{ id: string; thumbnailUrl: string | null }> };
  try {
    listed = await api.get(`trips/${tripId}/photos`, { searchParams: { limit: '100' } }).json();
  } catch {
    return;
  }

  await offlineDb.cacheTripPhotos(tripId, listed);

  const alreadyStored = await offlineDb.getPhotoFilePaths(tripId);

  for (const photo of listed.photos.slice(0, MAX_CACHED_THUMBNAILS_PER_TRIP)) {
    if (signal?.aborted) return;
    if (!photo.thumbnailUrl || alreadyStored[photo.id]) continue;

    try {
      // Straight fetch, not the API client: this is a public bucket URL, and
      // attaching our Authorization header to it would be pointless at best.
      const response = await fetch(photo.thumbnailUrl, { signal });
      if (!response.ok) continue;

      const path = await savePhotoFile(photo.id, 'thumbnail', await response.blob());
      await offlineDb.recordPhotoFile(photo.id, tripId, path);
    } catch {
      // One missing thumbnail is not worth abandoning the rest.
    }
  }
}

export async function prefetchTripsForOffline(
  queryClient: QueryClient,
  trips: TripResponse[],
  options: PrefetchOptions = {},
): Promise<PrefetchResult> {
  const { signal, startDelayMs = START_DELAY_MS, now = new Date() } = options;
  const result: PrefetchResult = { fetched: 0, skipped: 0, failed: 0 };

  if (!offlineDb.sessionUserId || trips.length === 0) return result;

  // Let the screen the user is looking at finish first.
  if (startDelayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, startDelayMs));
  }
  if (signal?.aborted) return result;

  let index: CachedTripIndexEntry[] = [];
  try {
    index = await offlineDb.getCachedTripIndex();
  } catch {
    // No index means everything looks missing, which is the safe direction.
  }

  // Ordered so that if the connection dies halfway through, what made it is
  // the part worth having.
  const ordered = prioritiseTrips(trips, now)
    .filter((trip) => needsPrefetch(trip.id, index, now))
    .slice(0, MAX_TRIPS);

  if (ordered.length === 0) {
    result.skipped = trips.length;
    return result;
  }

  const { tripsApi } = await import('@/features/trips/api/trips.api');
  const queue = [...ordered];

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      if (signal?.aborted) return;

      const trip = queue.shift();
      if (!trip) return;

      try {
        const detail = await tripsApi.getTripById(trip.id);
        if (signal?.aborted) return;

        await offlineDb.cacheTrip(trip.id, detail);
        await prefetchExpenses(trip.id, signal);
        await prefetchPhotos(trip.id, signal);
        // Seeded stale: the copy on screen, if any, should still refresh on its
        // own schedule rather than being frozen by a background write.
        if (queryClient.getQueryData(tripKeys.detail(trip.id)) === undefined) {
          queryClient.setQueryData<TripDetailResponse>(tripKeys.detail(trip.id), detail, {
            updatedAt: 0,
          });
        }
        result.fetched++;
      } catch {
        // One unreachable trip must not stop the rest; it stays un-prefetched
        // and gets another go next launch.
        result.failed++;
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ordered.length) }, () => worker()));

  result.skipped = trips.length - ordered.length;
  return result;
}

export default prefetchTripsForOffline;
